import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPacksUnreadSummary } from '../components/documents/packApi';
import { getUnreadMessagesCount } from '../components/chat/chatApi';
import { getTasksUnreadCount } from '../components/tasks/taskApi';
import { sumBadgeTotals, computeChatBadgeCount } from '../utils/badgeCounts';

const EMPTY_BY_USER = {};
const EMPTY_TOTALS = { tasks: 0, documents: 0, chat: 0 };

async function fetchBadgesForUser(userId) {
    const [docsSummary, chatSummary, tasksUnread] = await Promise.all([
        getPacksUnreadSummary(userId).catch(() => ({})),
        getUnreadMessagesCount(userId).catch(() => ({})),
        getTasksUnreadCount(userId).catch(() => 0),
    ]);

    return {
        tasks: Number(tasksUnread) || 0,
        documents: Number(docsSummary?.total) || 0,
        chat: computeChatBadgeCount(chatSummary?.unread_count, chatSummary?.manual_unread),
        hasEmptyBankPack: Boolean(docsSummary?.has_empty_active_bank_pack),
    };
}

export function useAccountBadges(users, options = {}) {
    const {
        enabled = true,
        refreshInterval = 15000,
        refreshKey = 0,
    } = options;

    const userIds = useMemo(
        () => (users || []).map((u) => Number(u.user_id)).filter((id) => id > 0),
        [users]
    );

    const [byUserId, setByUserId] = useState(EMPTY_BY_USER);
    const [totals, setTotals] = useState(EMPTY_TOTALS);
    const [hasEmptyBankPack, setHasEmptyBankPack] = useState(false);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!userIds.length) {
            setByUserId(EMPTY_BY_USER);
            setTotals(EMPTY_TOTALS);
            setHasEmptyBankPack(false);
            return;
        }

        setLoading(true);
        try {
            const entries = await Promise.all(
                userIds.map(async (userId) => {
                    try {
                        const badges = await fetchBadgesForUser(userId);
                        return [userId, badges];
                    } catch (error) {
                        console.warn('account badges failed for', userId, error);
                        return [userId, { tasks: 0, documents: 0, chat: 0, hasEmptyBankPack: false }];
                    }
                })
            );

            const nextByUser = Object.fromEntries(entries);
            const { totals: nextTotals, hasEmptyBankPack: nextBank } = sumBadgeTotals(nextByUser);
            setByUserId(nextByUser);
            setTotals(nextTotals);
            setHasEmptyBankPack(nextBank);
        } finally {
            setLoading(false);
        }
    }, [userIds]);

    const patchChatBadge = useCallback((userId, chatCount) => {
        const uid = Number(userId);
        const count = Number(chatCount) || 0;
        if (!uid) return;
        setByUserId((prev) => {
            const row = prev[uid] || { tasks: 0, documents: 0, chat: 0, hasEmptyBankPack: false };
            const next = { ...prev, [uid]: { ...row, chat: count } };
            const { totals: nextTotals, hasEmptyBankPack: nextBank } = sumBadgeTotals(next);
            setTotals(nextTotals);
            setHasEmptyBankPack(nextBank);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!enabled || !userIds.length) {
            setByUserId(EMPTY_BY_USER);
            setTotals(EMPTY_TOTALS);
            setHasEmptyBankPack(false);
            return undefined;
        }

        refresh();
        const timer = setInterval(refresh, refreshInterval);
        return () => clearInterval(timer);
    }, [enabled, refresh, refreshInterval, userIds, refreshKey]);

    return {
        byUserId,
        totals,
        hasEmptyBankPack,
        loading,
        refresh,
        patchChatBadge,
    };
}
